import { Typography, Box, IconButton, CircularProgress, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTokenClaims } from '../security/useTokenClaims'
import { Employee } from '../types/Employee/Employee'
import { useNavigate, useParams } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { getDirectReports } from '../services/shiftEndReportService'
import { ShiftEndSelectIndirect } from '../components/reports/shift-end/ShiftEndSelectIndirect'
import dayjs, { Dayjs } from 'dayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { useProjectFeaturesContext } from '../components/project/contexts/ProjectFeaturesContext'

const ShiftEndIndirectSelectionPage = ({ department }: { department: string }) => {
	const { id } = useTokenClaims()
	const [indirectWorkers, setIndirectWorkers] = useState<Employee[]>()
	const [loading, setLoading] = useState(false)
	const [shiftDate, setShiftDate] = useState<Date>(new Date())
	const params = useParams()
	const navigate = useNavigate()
	const { projectTimeZone } = useProjectFeaturesContext()
	const loadData = async () => {
		setLoading(true)
		const supervisorId = params.id ? parseInt(params.id) : id
		try {
			const found = await getDirectReports(supervisorId, department, shiftDate)
			if (found.length === 0) {
				setIndirectWorkers([])
			} else {
				setIndirectWorkers(found)
			}
		} catch (error) {
			setIndirectWorkers([])
		} finally {
			setLoading(false)
		}
	}
	useEffect(() => {
		;(async () => {
			await loadData()
		})()
	}, [params.id, shiftDate])

	const navigateBack = () => {
		switch (indirectWorkers?.[0]?.role.title) {
			case 'QualityControlManager':
				navigate('/inspections')
				break
			case 'QualityControl':
				navigate('/qcReports')
				break
			case 'LineTechManager':
				navigate('/surveys')
				break
			case 'LineTech':
				navigate('/ltReports')
				break
			case 'Material':
				navigate('/mReports')
				break
			case 'MaterialManager':
				navigate('/material')
				break
			default:
				navigate('/')
				break
		}
	}

	return (
		<>
			<Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
				<Box
					display={'flex'}
					justifyContent={'space-between'}
					alignItems={'center'}
					paddingTop={'1rem'}
				>
					<Box display={'flex'} alignItems={'center'}>
						<IconButton color='default' onClick={navigateBack}>
							<ArrowBackIcon sx={{ height: '2rem', width: 'auto' }} />
						</IconButton>

						<Typography
							sx={{
								fontSize: '1.5rem',
							}}
						>
							{indirectWorkers && indirectWorkers.length > 0
								? indirectWorkers[0].role.title.replace(/([a-z])([A-Z])/g, '$1 $2') + 's'
								: 'Indirects'}
						</Typography>
					</Box>
					<Box sx={{ marginRight: '1rem', display: 'flex', gap: '1rem' }}>
						<DatePicker
							label='Pick a date'
							value={shiftDate}
							onChange={(date: Dayjs | null) => {
								if (date) {
									const utcDate = date.tz(projectTimeZone || 'UTC').utc()
									setShiftDate(utcDate.toDate())
								}
							}}
							shouldDisableDate={(date: Dayjs) => {
								const today = dayjs().startOf('day')
								const dateToCheck = date.startOf('day')
								return dateToCheck.isAfter(today)
							}}
							renderInput={(props) => <TextField {...props} />}
						/>
					</Box>
				</Box>
				<Box
					sx={{
						display: 'flex',
						flex: 1,
						overflowY: 'auto',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					{loading ? (
						<CircularProgress color='success' />
					) : indirectWorkers?.length === 0 ? (
						<Typography variant='h6'>👥 No indirect workers available</Typography>
					) : (
						<ShiftEndSelectIndirect
							indirectWorkers={indirectWorkers ?? []}
							postConfirmLoadData={loadData}
							department={department}
							shiftDate={shiftDate}
						/>
					)}
				</Box>
			</Box>
		</>
	)
}

export default ShiftEndIndirectSelectionPage
